import { initializeApp } from 'firebase/app';
import Cookies from 'js-cookie';
import LocalForage from 'localforage';
import 'localforage-getitems';
import Vue from 'vue';

import type { ApolloProvider } from 'vue-apollo';
import type { Route } from 'vue-router';
import type { Store } from 'vuex';

import type { Context } from '@/@types/ssr';

import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';

import { OnboardingStatus } from '@/enums/onboarding-status';
import { WebLocale } from '@/enums/web-locale';

import { applyRoute } from '@/helpers/state-helper';
import { TrackingAppId, TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { createRouter } from '@/routing';
import { globalRoutes } from '@/routing/global';

import {
	TrackingProvider,
	TrackingProviderGoogleAnalytics,
	TrackingProviderGoogleTagManager,
	TrackingProviderSnapchatPixel,
	TrackingProviderSnowplow,
	TrackingProviderTwitterPixel,
} from '@/helpers/tracking/providers';
import { STORE_NAMESPACE } from '@/store';
import { ReadyType } from '@/stores/ready.store';
import { generateJwId, generateUUIDv4 } from './helpers/uuid-helper';
import { getVm } from './helpers/vm-helper';
import { initArbitrage } from './helpers/composables/useArbitrage';
import { activeSubgenreCountriesWebLocales } from './constants/subgenres';
import { detectDomain, handleSubdomainSwitch } from '@/routing/helpers/subdomain-helper';

export {
	initApp,
	initClientStorage,
	initFirebase,
	initJwId,
	initLanguage,
	initLocale,
	initRouter,
	initSPA,
	initSSR,
	initStore,
	initStoreWatchers,
	initTrackers,
	initUncriticalRequests,
	jwIdServerClientHandover,
	refreshToken,
};

const MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 Days
const JWID_COOKIE = 'jw_id' as const;

/**
 * Generates a valid JustWatch ID if needed and sets
 * it in a cookie for the rest of the app to use.
 */
function initJwId(context: Context): boolean {
	if (process.server) return !(JWID_COOKIE in context.req.cookies);
	if (document.cookie.includes(JWID_COOKIE)) return false;

	const jwId = generateJwId();

	fetch(JW_CONFIG.GRAPHQL_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			query: `mutation RegisterDeviceId { registerDeviceId(input: "${jwId}") { deviceId } }`,
		}),
	})
		.then(resp => (resp?.ok ? null : resp.json()))
		.then(json => {
			const error = (json?.errors ?? [])[0];
			if (error) throw error;
		})
		.catch(error => {
			import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) => {
				captureMessageForSentry(
					'[Fetch GraphQL RegisterDeviceId]:',
					{ error, where: '[main.common.ts]: initJwId' },
					'error'
				);
			});
		});

	document.cookie =
		`${JWID_COOKIE}=${jwId}` +
		`; expires=${new Date(+new Date() + MAX_AGE).toUTCString()}` +
		'; path=/; secure; SameSite=None';

	return true;
}

async function initFirebase() {
	try {
		const firebaseConfig = {
			apiKey: JW_CONFIG.FIREBASE_API_KEY,
			authDomain: JW_CONFIG.FIREBASE_AUTH_DOMAIN,
			projectId: JW_CONFIG.FIREBASE_PROJECT_ID,
			storageBucket: JW_CONFIG.FIREBASE_STORAGE_BUCKET,
			messagingSenderId: JW_CONFIG.FIREBASE_MESSAGING_SENDER_ID,
			appId: JW_CONFIG.FIREBASE_APP_ID,
		};
		initializeApp(firebaseConfig);
	} catch (err) {
		const error: any = err;

		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry('Firebase Init Issue:', { error, where: '[main.common.ts] initFirebase()' }, 'error');
	}
}

/**
 * Client Only Method
 *
 * @param context
 */
async function refreshToken(context: Context) {
	const { store } = context;
	// This order is important, we want to try refresh the token before initializing the store
	// and making the requests to loadList and loadSettings. The user would be logged out if the refreshToken fails
	if (store.getters['user/isLoggedIn']) {
		const token = await store.dispatch('user/refreshToken');

		// If token isn't returned then there was an error returning token
		if (!token) {
			context.showRefreshTokenError = true;

			TrackingHelper.trackEvent('refresh_token_error', {
				action: 'main_ssr_refresh',
			});
		}
	}

	if (store.state.user.accessToken) {
		const { currentUser } = await import('@/helpers/user-helper');

		const user = await currentUser();

		if (!user) {
			await store.dispatch('user/logout');
		}
	}
}

async function initClientStorage() {
	let driver;
	if (LocalForage.supports(LocalForage.LOCALSTORAGE)) {
		driver = LocalForage.LOCALSTORAGE;
	}

	if (!driver) {
		throw new Error(`[Local storage] local forage drivers not available`);
	}

	LocalForage.config({
		name: STORE_NAMESPACE,
		driver: driver,
	});
}

async function initApp(context: Context) {
	const { store, webLocale, url } = context;
	// these requests will be checked first, if they have been done on the server already and will skip accordingly
	if (process.server || !process.ssr) {
		await coreAppRequests(store);
	}

	if (!context.store.state.user.onboardingStatus) {
		const { loadOnBoardingStatus } = await import('@/helpers/onboarding-helper');
		await loadOnBoardingStatus(store, webLocale, url);
	}
}

function setPageViewUUID(context: Context) {
	const pageViewUUID = generateUUIDv4();
	context.store.dispatch('tracking/setPageViewUUID', pageViewUUID);
}

async function trackPage(context: Context, to: Route, from: Route, metadataPromise: Promise<any> | undefined) {
	setPageViewUUID(context);
	const { meta, fullPath } = to;
	const metaReady = metadataPromise ?? Promise.resolve();

	// ###### TRACK PAGE ######
	// We deliberately do not `await trackPage` here, to avoid blocking the router
	// on slow async work. Instead, we ensure critical prerequisites (like urlMetadata)
	// are ready before calling it.
	// Here the async actions that happen in trackPage, that are already resolved beforehand and don't yield additional awaits:
	// - urlMetadata: resolved
	// - getContexts: only unresolved if there is an unknown pagetype
	const metaContexts = (meta?.contexts || []) as string[];

	// we track page views ourselves for title detail pages and (nearly all) sport pages
	const sportPageExceptions = [
		// 'app.sports.all', // this isn't included because it doesn't have any sport context
		'app.sports.overview',
		'app.sports.competition', // @todo implement with sport context
		'app.sports.event',
		'app.sports.team|competition',
	];

	//handle first routing subgenre in routingConfig, assign permanentAudienceSubgenre in beforeEnter() subgenre route
	const firstRoutingIsSubgenre =
		(from?.name === null || from?.name === 'app.refreshtoken') && (to.name || '').includes('subgenre');

	if (!metaContexts.includes('title') && !sportPageExceptions.includes(to.name || '') && !firstRoutingIsSubgenre) {
		await metaReady;
		TrackingHelper.trackPage(fullPath);
	}
}

async function initRouter(context: Context, apolloProvider?: ApolloProvider) {
	const doEveryPageView = async (context: Context, to: Route, from?: Route): Promise<any> => {
		// temporary fix for dev env @to-do fix server
		const isRefresh = to.path.indexOf('/refresh') === 0;
		const isBadAnon = from?.name !== 'app.refreshtoken' && !to.name && to.path !== '/';
		if (isRefresh || isBadAnon) return;

		// @note: setActiveRoute needs to happen before fetchUrlMetadata
		// maybe it's better to merge these 2, since they aren't called anywhere else, either
		context.store.dispatch('routing/setActiveRoute', { activeRoute: to, router: context.router });
		// we should tie pagesVisited to activeRoute.
		// with a flaky connection there is a chance that we transitioned to a new route (activeRoute),
		// but the pagesVisited hasn't reflected that because it's still awaiting urlMetadata.
		context.store.commit('tracking/INCREASE_PAGES_VISITED');

		// Home Page on firstRouting has from.path = '/' and to.path = '/', trackPage awaits urlMetadataPromise - if we dont dispatch routing/fetchUrlMetadata here this promise never resolves
		const firstRouting = from?.name === null || from?.name === 'app.refreshtoken';
		if (to.path !== from?.path || firstRouting) {
			if (apolloProvider) {
				return context.store.dispatch('routing/fetchUrlMetadata', { url: to.path, apolloProvider });
			} else {
				return context.store.dispatch('routing/fetchUrlMetadata', to.path);
			}
		}

		return Promise.resolve();
	};

	// check for first routing and possibly update store state from url (e.g. filters from url)
	context.router.beforeEach(async (to: Route, from: Route, next: () => void) => {
		const firstRouting = from?.name === null || from?.name === 'app.refreshtoken';

		// Handle route changes that switch subdomain
		if (process.client && to.name) {
			await handleSubdomainSwitch(context, from, to);
		}

		// persist scroll position for each tab
		if (!firstRouting && process.client) {
			const tab = from?.meta?.tab === 'sports' ? `${from?.meta?.tab}-${from?.meta?.data}` : from?.meta?.tab;
			context.store.commit('routing/SET_SCROLL_POSITION', { tab, y: window.scrollY });
		}

		if (firstRouting && process.client) {
			initArbitrage(to, context.store.getters['language/country']);
		}

		// fire doEveryPageView only when:
		// - on the server
		// OR
		// - is not first routing `!firstRouting`
		// OR
		// - (first routing `firstRouting` AND activeRoute hasn't been set by the server `!context.store.state.routing.activeRoute`)
		// OR
		// - is SPA app BUILD_CONFIG.globals.BUILD_TARGET === 'SPA'

		let promise: Promise<any> | undefined;
		if (
			process.server ||
			!firstRouting ||
			(firstRouting && !context.store.state.routing.activeRoute) ||
			BUILD_CONFIG.globals.BUILD_TARGET === 'SPA'
		) {
			promise = doEveryPageView(context, to, from);
			if (process.server) {
				// we only need the server to await the promise, the client can move forward already
				await promise;
			}
		} else if (process.client && firstRouting) {
			// Hydration path (cached *or* fresh SSR):
			// On the very first client render we skip doEveryPageView,
			// because the server already fetched and serialized the metadata into __DATA__.
			// Note: the serialized urlMetadataPromise itself is not a real Promise
			// (it becomes just a plain object in __DATA__), so awaiting it would hang indefinitely.
			// To prevent that, we replace it with either:
			//   - Promise.resolve() if metadata for this path is already present,
			//   - or a fresh fetchUrlMetadata() call as a fallback if data is missing/stale.

			const haveMetaForPath =
				context.store.state.routing?.activeRoute?.path === to.path &&
				!!context.store.state.routing?.urlMetadata;

			if (haveMetaForPath) {
				promise = Promise.resolve();
				context.store.commit('routing/SET_URL_METADATA_PROMISE', promise);
			} else {
				// fallback: metadata missing/stale – fetch now
				if (apolloProvider) {
					promise = context.store.dispatch('routing/fetchUrlMetadata', { url: to.path, apolloProvider });
				} else {
					promise = context.store.dispatch('routing/fetchUrlMetadata', to.path);
				}
			}
		}

		// Track page view on client only
		if (process.client) {
			trackPage(context, to, from, promise);
		}

		// check first page view
		if ((firstRouting && process.server) || (process.client && to.path !== from.path)) {
			// @todo translate route parameters to filters if there are any
			await applyRoute(to, context.store);
		}

		next();
	});

	context.router.afterEach(async (to: Route & { meta: { onActivate: any } }, from: any) => {
		const onActivate = to.meta.onActivate;
		// call activation function from routes
		if (onActivate) {
			onActivate(context.store, to, from);
		}
	});
}

async function initStore(context: Context) {
	const { store, webLocale, url } = context;

	store.dispatch('app/setSeoUser', context.ssrData.isSeoUser);

	if (process.server) {
		// xServeTrack is only available on the server
		// context.ssrData is overwritten by the client
		store.dispatch('app/setXServeTrack', context.ssrData.xServeTrack);
	}

	// NOTE(valerio.mazza): failsafe for filter.store to remove after a proper refactor of the filter.store
	if (!store.state.filter[webLocale]) {
		// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
		store.dispatch('filter/initFiltersPerLocale', webLocale, { root: true });
	}

	if (store.state.user.jwId) {
		Vue.$jw.ready?.setReady(ReadyType.JW_ID);
	}

	if (store.state.user.jwId && !url.includes('/refresh')) {
		await store.dispatch('user/loadSettings');

		// Should fix hydration issues when logging in
		await store.dispatch('user/setUserRole');
	}
}

async function initSPA(context: Context, apolloProvider: ApolloProvider): Promise<any[]> {
	// initialize id service
	if (context.store.state.user.jwId) {
		context.ssrData = { isSeoUser: false };
		return await context.store.dispatch('user/setJwId', context.store.state.user.jwId);
	} else {
		context.ssrData = { isSeoUser: true };
		return await context.store.dispatch('user/fetchJwId', apolloProvider);
	}
}

function initSSR(context: Context, isSeoUser: boolean) {
	context.ssrData = {
		isSeoUser,
		xServeTrack: null,
	};

	if (process.server) {
		// get canary flag, e.g. canary-alpha if present
		const xServeTrack = context.req.headers['x-serve-track'];
		context.ssrData.xServeTrack = xServeTrack || null;
	}
}

function initUncriticalRequests(context: Context) {
	if (process.server || !process.ssr) {
		return [context.store.dispatch('footer/fetchTopTitles'), context.store.dispatch('footer/fetchTopArticles')];
	}
	return [];
}

function jwIdServerClientHandover(store: Store<any>) {
	const localJwUser = JSON.parse(localStorage.getItem('jw/user') ?? 'null');
	const jwId = Cookies.get(JWID_COOKIE) ?? localJwUser?.jwId;

	return store.dispatch('user/setJwId', jwId);
}

async function initStoreWatchers(context: Context) {
	const { i18n, store, router, url } = context;

	// #### webLocale ####
	store.watch(
		(state: any) => state.language.webLocale,
		async (webLocale: WebLocale, oldWebLocale: WebLocale | undefined) => {
			if (webLocale !== oldWebLocale && !store.state.filter[webLocale]) {
				store.dispatch('filter/initFiltersPerLocale', webLocale);
			}

			if (webLocale !== oldWebLocale) {
				if (!router) {
					// change locale or create router (and change locale)
					const currentDomain = detectDomain(window.location.hostname);
					context.router = createRouter(webLocale, store, currentDomain);
				} else {
					// router is already initialized, change country on the fly
					router.changeLocale(webLocale, store);
				}

				const currentUrl = store.state.routing.activeRoute.path;
				initLanguage({ i18n, store, webLocale, router, url: currentUrl });

				store.dispatch('user/loadSettings');

				store.dispatch('constant/fetchPackages');
				store.dispatch('constant/fetchConstants');
				store.dispatch('footer/fetchTopTitles');
				store.dispatch('footer/fetchTopArticles');

				if (activeSubgenreCountriesWebLocales.includes(webLocale)) {
					// refetch subgenre translations and fullPaths info
					store.dispatch('constant/fetchSubgenres');
				}

				// Update Sentry tag when country changes
				if (process.client) {
					const { updateSentryTags } = await import('@/helpers/sentry-helper');
					updateSentryTags({ store_country: store.getters['language/country'] });
				}
			}
		},
		{ immediate: false }
	);

	// #### overriddenLanguage ####
	store.watch(
		(state: any) => state.language.overriddenLanguage,
		async (language: string) => {
			if (language) {
				const activeRoute = getVm()?._route ?? router?.match(context.url);
				await store.dispatch('language/fetchTranslation', { language, activeRoute });
				await store.dispatch('language/setLanguage', { language, i18n });
			}
		}
	);

	// watch accessToken when user login set onboarding accordingly
	store.watch(
		(state: any) => state.user.accessToken,
		async (value: string) => {
			if (value) {
				await store.dispatch('user/loadSettings');
				const { loadOnBoardingStatus } = await import('@/helpers/onboarding-helper');
				const webLocale = store.state.language.detectedWebLocale || store.state.language.webLocale;
				await loadOnBoardingStatus(store, webLocale, url);
			}
		}
	);

	// watch loggedInProviders when user login set onboarding accordingly
	store.watch(
		(state: any) => state.user.loggedInProviders,
		async (providers: Array<any>) => {
			if (providers.length > 0 && store.state.user.settings.taste_survey_completed) {
				store.dispatch('user/saveOnboardingStatus', OnboardingStatus.USER);
			} else if (providers.length > 0 && !store.state.user.settings.taste_survey_completed) {
				store.dispatch('user/saveOnboardingStatus', OnboardingStatus.SIGNED_UP);
			}
		},
		{ immediate: false }
	);
}

/*************       initLocale        *************/

async function initLocale(context: Partial<Context>) {
	let initAppLocale: WebLocale;
	const { store, url = '' } = context;

	const path = url.split('?')[0];
	const slug = /^\/[\w\d]+/.exec(url)?.[0] || '';
	const isGlobalRoute = globalRoutes.includes(path) || globalRoutes.includes(slug);

	if (isGlobalRoute) {
		initAppLocale = await getWebLocaleFromLocation(context);
	} else {
		initAppLocale = await getWebLocaleFromPath(context, path);
	}

	await store.dispatch('language/changeWebLocale', initAppLocale);

	return initAppLocale;
}

async function getWebLocaleFromLocation(context: Partial<Context>) {
	let locale =
		context.store.state.user.persistedWebLocale ||
		(process.client && context.store.state.language.detectedWebLocale);

	// For Global routes we still need to know the location in the server
	// if server OR if SPA app
	if (!locale && process.server) {
		// This header is only meant for global routes because the region is not in the URL
		// IN SSR, DO NOT PASS THIS TO CLIENT (use `getUserLocationCountryCode` instead)
		const region = context.req.headers['x-client-geo-region'];
		locale = region?.toLowerCase();
	}

	// get Browser Locale as last resort
	// happens for SPA visitors that do not pass through SSR first
	if (!locale && process.client) {
		const { getUserLocationCountryCode } = await import('@/helpers/geo-location-helper');
		locale = getUserLocationCountryCode()?.toLowerCase() as WebLocale;
	}

	// hard fallback to US just in case
	if (!locale) locale = 'us';

	context.store.dispatch('language/setDetectedWebLocale', locale);
	return context.store.state.language.detectedWebLocale;
}

async function getWebLocaleFromPath(context: Partial<Context>, path: string) {
	const { store } = context;
	const locale = path && path.length >= 3 && path.split('/')[1];

	const webLocale = locale && COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;
	await store.dispatch('language/setDetectedWebLocale', webLocale);
	return store.state.language.detectedWebLocale;
}

/*************       initLanguage        *************/

async function initLanguage(context: Partial<Context>): Promise<any> {
	const { i18n, store, webLocale, router } = context;
	let language = store.state.user.persistedOverriddenLanguage;

	await store.dispatch('language/loadLocales');

	// Get language from localStorage or detect browser language
	if (store.state.user.persistedOverriddenLanguage) {
		language = store.state.user.persistedOverriddenLanguage;
		store.dispatch('language/setDetectedLanguage', language);
	} else {
		store.dispatch('language/setLanguageFromLocale', webLocale);
		language = store.state.language.overriddenLanguage;
	}

	// Hard fall back to en
	if (!language) {
		language = 'en';

		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry('[Language] not able to detect language, hard fallback to en', {
			where: '[main.common.ts] initLanguage Failed to init',
		});
	}

	const activeRoute = router?.match(context.url || `/${webLocale}`); // when changing webLocale within the app, `context.url` isn't set
	await store.dispatch('language/fetchTranslationAndSetLanguage', { i18n, language, activeRoute });
	await store.dispatch('meta/setAppDirection', { language });

	return language;
}

/**
 *
 */
async function coreAppRequests(store: Store<any>) {
	let parallelRequests: Promise<any>[] = [];

	// Fetch Packages early (2 attempts)
	parallelRequests = [
		...parallelRequests,
		store.dispatch('constant/fetchPackages'),
		store.dispatch('constant/fetchConstants'),
	];

	if (activeSubgenreCountriesWebLocales.includes(store.state.language.webLocale)) {
		parallelRequests.push(store.dispatch('constant/fetchSubgenres'));
	}

	return Promise.all(parallelRequests);
}

async function initTrackers(store: Store<any>, force: boolean = false) {
	const { hasDoNotTrack } = await import(/* webpackChunkName: "constent-helper" */ '@/helpers/consent-helper');
	try {
		TrackingHelper.setStore(store);
		const consents = store.state.user.settings?.jw_consents || {};
		const deviceDoNotTrack = false;
		const jwId = store.state.user.jwId;
		let snowPlowJwId = jwId;
		if (!snowPlowJwId) {
			const randDigits = Array(17)
				.fill(0)
				.map(_ => Math.random().toString(36).charAt(2))
				.join('');
			snowPlowJwId = `fake-${randDigits}`;
		}
		// initialize trackers
		// pass over the instance to the store for snowplow provider beeing
		// able to compile a list of contexts from global state
		const { TrackingProviderFacebookPixel, TrackingProviderTiktokPixel } = await import(
			/* webpackChunkName: "tracking-providers" */ '@/helpers/tracking/providers'
		);

		// we have a few trackers that use the underlying gtag-tech.
		// to make it all work together, we need to
		window.dataLayer = window.dataLayer || [];
		window.gtag =
			window.gtag ||
			function () {
				// eslint-disable-next-line
				window.dataLayer.push(arguments);
			};
		// defaulting consent for google products (using gtag) is GDPR compliant,
		// because we only load said libraries after user consent has been given (through Usercentrics).
		// note: there is the possibility that a user consents to GA's personalisation, but not to GAM's personalisation.
		// this is a tough cookie to solve, but usually these things are solved with the TC string.
		// if not, you'd have to query the consent and manually set setRequestNonPersonalizedAds(1) on GAM.
		window.gtag('consent', 'default', {
			ad_storage: 'granted',
			ad_user_data: 'granted',
			ad_personalization: 'granted',
			analytics_storage: 'granted',
			security_storage: 'granted',
		});

		// initialize first with trackers that on web and mobile
		const trackers = [
			// SNOWPLOW
			new TrackingProviderSnowplow({
				userId: snowPlowJwId,
				appId: TrackingAppId,
				cookieDomain: global.JW_CONFIG.SNOWPLOW_COLLECTOR_ENDPOINT,
				platform: 'web',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents),
				eventMethod: 'post',
				postPath: '/5e87d/bx6',
			}),
			// GOOGLE TAG MANAGER
			new TrackingProviderGoogleTagManager({
				containerId: 'GTM-53RWB5',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['google']),
			}),
			// GOOGLE ANALYTICS
			new TrackingProviderGoogleAnalytics({
				id: 'G-43S8GVCWJK',
				debug: {
					enabled: false, // process.env.NODE_ENV === 'development',
				},
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['google']),
				userId: jwId,
			}),
			// FACEBOOK PIXEL
			new TrackingProviderFacebookPixel({
				pixelId: '416036085213374',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['facebook']),
			}),
			// TIKTOK PIXEL
			new TrackingProviderTiktokPixel({
				pixelId: 'C5O26T5O3VNUQLVLL8P0',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['tiktok']),
			}),
			new TrackingProviderTwitterPixel({
				pixelId: 'o0dvi',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['twitter']),
			}),
			// SNAPCHAT PIXEL TRACKER
			new TrackingProviderSnapchatPixel({
				pixelId: '5b32c5b6-8e7f-4495-93ab-0061758f9a18',
				doNotTrack: hasDoNotTrack(deviceDoNotTrack, consents, ['snapchat']),
				email: store.state.user.email,
			}),
		].filter(Boolean) as TrackingProvider[];
		TrackingHelper.initializeTrackers(trackers, force);
	} catch (err) {
		const error: any = err;
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry('Main Init Issue:', { error, where: '[main.common.ts] initializeTracking' }, 'error');
	}
}
